<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'FieldsetFixedAssetGeneral',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
    fixedAsset: {
      type: Object,
      required: true,
    },
    labelAlign: {
      type: String,
      required: false,
      default: 'right',
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const fixedAssetGroups = ref(null);
  const currencies = ref(['HUF', 'EUR', 'USD']);
  const units = ref(['ha', 'm2']);
  const fixedAssetTypes = ref({
    general: $fn.value.pgettext('FixedAssetTypes', 'General'),
    waste_bin: $fn.value.pgettext('FixedAssetTypes', 'Waste Bin'),
  });
  const ux = ref([]);

  const $data = computed(() => ({
    fixedAssetGroups: fixedAssetGroups.value,
    currencies: currencies.value,
    units: units.value,
    fixedAssetTypes: fixedAssetTypes.value,
    ux: ux.value,
  }));

  const $$load_fixedAssetGroups = () => {
    return $fn.value.fetch('list_group', {
      parameters: { type: 'fixed_asset' },
    });
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'fixedAssetGroups' },
      { name: 'currencies' },
      { name: 'units' },
      { name: 'fixedAssetTypes' },
      { name: 'ux' },
    ],
    loaders: { fixedAssetGroups: $$load_fixedAssetGroups },
    ux: {},
    dataRefs: { fixedAssetGroups, currencies, units, fixedAssetTypes, ux },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  watch(
    () => props.fixedAsset,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('fixedAsset');
    }
  );
  watch(
    () => props.labelAlign,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('labelAlign');
    }
  );

  const act_7315092382398562_reloadComponentData_04d6cetj2 =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_i74dsxwl5 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_04d6cetj2,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_i74dsxwl5],
    event: 'reload',
  });

  const actions = {
    inst_evh_7315092382398562_reload_i74dsxwl5,
    inst_evh_7315092382398562_reload,
  };

  const evh_7315092382398562_reload_i74dsxwl5 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_i74dsxwl5.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpUsercontrol
    :loading="loading"
    :reloading="reloading"
    :loaded="loaded"
    :error="error"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <ItpFormGroup
      :label-cols-md="4"
      :content-cols-md="8"
      name="asset_type"
      :label="pgettext('FixedAsset', 'Asset Type')"
      :labelAlign="labelAlign"
    >
      <ItpBadge variant="secondary" class="px-2 shadow-none">
        <span>
          {{ fixedAssetTypes[fixedAsset.asset_type] }}
        </span>
      </ItpBadge>
    </ItpFormGroup>
    <ItpFormGroup
      :label-cols-md="4"
      :content-cols-md="8"
      name="name"
      :label="pgettext('FixedAsset', 'Name')"
      :labelAlign="labelAlign"
      required
    >
      <ItpFormInput name="name" v-model="fixedAsset.name" required autofocus>
      </ItpFormInput>
    </ItpFormGroup>
    <ItpFormGroup
      :label-cols-md="4"
      :content-cols-md="4"
      name="quantity"
      :label="pgettext('FixedAsset', 'Quantity')"
      :labelAlign="labelAlign"
      required
    >
      <ItpFormInput
        type="number"
        name="quantity"
        size="md"
        v-model="fixedAsset.quantity"
      >
      </ItpFormInput>
    </ItpFormGroup>
    <ItpFormGroup
      :label-cols-md="4"
      :content-cols-md="8"
      name="inventory_no"
      :label="pgettext('FixedAsset', 'Inventory Number')"
      :labelAlign="labelAlign"
    >
      <ItpFormInput
        name="inventory_no"
        size="md"
        v-model="fixedAsset.inventory_no"
      >
      </ItpFormInput>
    </ItpFormGroup>
    <ItpFormGroup
      :label-cols-md="4"
      :content-cols-md="4"
      name="sku"
      :label="pgettext('FixedAsset', 'SKU')"
      :labelAlign="labelAlign"
    >
      <ItpFormInput name="sku" size="md" v-model="fixedAsset.sku">
      </ItpFormInput>
    </ItpFormGroup>
    <ItpFormGroup
      :label-cols-md="4"
      :content-cols-md="8"
      name="description"
      :label="pgettext('FixedAsset', 'Description')"
      :labelAlign="labelAlign"
    >
      <ItpFormInput
        name="description"
        size="md"
        v-model="fixedAsset.description"
      >
      </ItpFormInput>
    </ItpFormGroup>
    <ItpFormGroup
      :label-cols-md="4"
      :content-cols-md="8"
      name="type_no"
      :label="pgettext('FixedAsset', 'Type Number')"
      :labelAlign="labelAlign"
    >
      <ItpFormInput name="type_no" size="md" v-model="fixedAsset.type_no">
      </ItpFormInput>
    </ItpFormGroup>
    <ItpFormGroup
      :label-cols-md="4"
      :content-cols-md="4"
      name="cost"
      :label="pgettext('FixedAsset', 'Cost')"
      :labelAlign="labelAlign"
    >
      <ItpFormInput
        type="number"
        name="cost"
        size="md"
        v-model="fixedAsset.cost"
      >
      </ItpFormInput>
    </ItpFormGroup>
    <ItpFormGroup
      :label-cols-md="4"
      :content-cols-md="4"
      name="gl_code"
      :label="pgettext('FixedAsset', 'General Ledger Code')"
      :labelAlign="labelAlign"
    >
      <ItpFormInput
        type="number"
        name="gl_code"
        size="md"
        v-model="fixedAsset.gl_code"
      >
      </ItpFormInput>
    </ItpFormGroup>
    <ItpFormGroup
      :label-cols-md="4"
      :content-cols-md="4"
      name="year_of_production"
      :label="pgettext('FixedAsset', 'Year of Production')"
      :labelAlign="labelAlign"
    >
      <ItpFormInput
        type="number"
        name="year_of_production"
        size="md"
        v-model="fixedAsset.year_of_production"
      >
      </ItpFormInput>
    </ItpFormGroup>
    <ItpFormGroup
      :label-cols-md="4"
      :content-cols-md="8"
      name="manufacturer"
      :label="pgettext('FixedAsset', 'Manufacturer')"
      :labelAlign="labelAlign"
    >
      <ItpFormInput
        name="manufacturer"
        size="md"
        v-model="fixedAsset.manufacturer"
      >
      </ItpFormInput>
    </ItpFormGroup>
    <ItpFormGroup
      :label-cols-md="4"
      :content-cols-md="8"
      name="serial_no"
      :label="pgettext('FixedAsset', 'Serial Number')"
      :labelAlign="labelAlign"
    >
      <ItpFormInput name="serial_no" size="md" v-model="fixedAsset.serial_no">
      </ItpFormInput>
    </ItpFormGroup>
    <ItpFormGroup
      :label-cols-md="4"
      :content-cols-md="8"
      name="category"
      :label="pgettext('FixedAsset', 'Category')"
      :labelAlign="labelAlign"
    >
      <ItpFormInput name="category" size="md" v-model="fixedAsset.category">
      </ItpFormInput>
    </ItpFormGroup>
    <ItpFormGroup
      :label-cols-md="4"
      :content-cols-md="8"
      name="groups"
      :label="pgettext('FixedAsset', 'Groups')"
      :labelAlign="labelAlign"
    >
      <ItpFormSearchableSelect
        name="groups"
        size="md"
        v-model="fixedAsset.groups"
        multiple
        :options="fixedAssetGroups"
        valueField="id"
        textField="name"
      >
      </ItpFormSearchableSelect>
    </ItpFormGroup>
  </ItpUsercontrol>
</template>
